import { Icons, Menu, Input } from '@pankod/refine-antd'
import { AppstoreOutlined, CloseCircleOutlined, FilterOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
interface Props {
  handleLoading?: (state: boolean) => void
  fetchQuestions?: (
    limit: number,
    currentPage: number,
    searchValue: string,
    userRole: string,
    userId: string,
    additionalFilters: any
  ) => Promise<void>
  handleFilters: (filters: any) => void
  totalNumberOfItems?: number
  limit?: number
  currentPage?: number
  userRole: string
  userId: string
  tableStateLoading?: boolean
  searchValue: string
  additionalFilters?: any
  setCurrentPageValue?: (pageNumber: number) => void
  hiddenMenu?: string[]
}

const AllQuestionFilter: React.FC<Props> = ({
  handleLoading,
  fetchQuestions,
  handleFilters,
  totalNumberOfItems,
  limit,
  currentPage,
  userRole,
  userId,
  tableStateLoading,
  searchValue,
  additionalFilters,
  setCurrentPageValue,
  hiddenMenu = [],
}) => {
  const [allFilterValues, setFilterValues] = useState<any>({})
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [activeFilters, setActiveFilters] = useState<any>({})
  const [searchQueries, setSearchQueries] = useState<any>({})
  const source = ''

  // Fetching filter values from backend
  const fetchFilterValues = async () => {
    try {
      // const response = await fetch(`http://localhost:3001/get-question-filter-values`, {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get-question-filter-values`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subjectId: activeFilters?.subject_id?.value || '',
          themeId: activeFilters?.theme_id?.value || '',
          subthemeId: activeFilters?.sub_theme_id?.value || '',
        }),
      })
      const filterData = await response.json()
      if (filterData) {
        setFilterValues(filterData)
      }
    } catch (error) {
      console.error('Failed to fetch filter values', error)
    }
  }

  useEffect(() => {
    fetchFilterValues()
  }, [activeFilters])

  const handleChange = async (filterKey: string, value: string, operator: string, key: string, label: string) => {
    const cascadeMap: Record<string, string[]> = {
      subject: ['theme', 'sub_theme', 'topic'],
      theme: ['sub_theme', 'topic'],
      sub_theme: ['topic'],
    }
    let newFilters = {}
    setActiveFilters((prev: any) => {
      const existingFilter = prev[filterKey]

      if (existingFilter && existingFilter.value === value) {
        return prev
      }

      // Start with previous filters
      let updatedFilter = { ...prev }

      // Clear dependent filters if the changed filter is a parent
      if (cascadeMap[key]) {
        cascadeMap[key].forEach(childKey => {
          const childFilterKey = questionFilters.find(f => f.key === childKey)?.filterKey
          if (childFilterKey) {
            delete updatedFilter[childFilterKey]
          }
        })
      }
      // Set the new selected filter
      updatedFilter[filterKey] = { value, operator, label, key }
      newFilters = {
        blooms_id: updatedFilter?.bloom_level_id?.value || '',
        difficulty: updatedFilter?.difficulty_level_id?.value || '',
        subject_id: updatedFilter?.subject_id?.value || '',
        theme_id: updatedFilter?.theme_id?.value || '',
        sub_theme_id: updatedFilter?.sub_theme_id?.value || '',
        topic_id: updatedFilter?.topic_id?.value || '',
        question_created_through: updatedFilter?.request_type?.value || '',
        status: updatedFilter?.status?.value || '',
      }
      handleFilters(newFilters)
      setSelectedKeys((prevKeys: any) => {
        const newKeys = cascadeMap[key] ? prevKeys.filter((k: string) => !cascadeMap[key].includes(k)) : [...prevKeys]

        if (!newKeys.includes(key)) {
          newKeys.push(key)
        }

        return newKeys
      })

      return updatedFilter
    })
    //fetching quesitons
    if (setCurrentPageValue && fetchQuestions && limit) {
      setCurrentPageValue(1)
      await fetchQuestions(limit, 1, searchValue, userRole, userId, newFilters)
    }
  }

  const handleRemoveFilter = async (key: string, filterKey: string, operator: string) => {
    const cascadeMap: Record<string, string[]> = {
      subject: ['theme', 'sub_theme', 'topic'],
      theme: ['sub_theme', 'topic'],
      sub_theme: ['topic'],
    }
    let newFilters = {}
    setActiveFilters((prev: any) => {
      const updated = { ...prev }
      delete updated[filterKey]
      if (cascadeMap[key]) {
        cascadeMap[key].forEach(childMenuKey => {
          const childFilterKey = questionFilters.find(f => f.key === childMenuKey)?.filterKey
          if (childFilterKey) delete updated[childFilterKey]
        })
      }
      newFilters = {
        blooms_id: updated?.bloom_level_id?.value || '',
        difficulty: updated?.difficulty_level_id?.value || '',
        subject_id: updated?.subject_id?.value || '',
        theme_id: updated?.theme_id?.value || '',
        sub_theme_id: updated?.sub_theme_id?.value || '',
        topic_id: updated?.topic_id?.value || '',
        question_created_through: updated?.request_type?.value || '',
        status: updated?.status?.value || '',
      }
      handleFilters(newFilters)
      return updated
    })

    setSelectedKeys((prev: any) => {
      const childKeysToRemove = cascadeMap[key] || []
      return prev.filter((item: string) => item !== key && !childKeysToRemove.includes(item))
    })
    if (setCurrentPageValue && fetchQuestions && limit) {
      setCurrentPageValue(1)
      await fetchQuestions(limit, 1, searchValue, userRole, userId, newFilters)
    }
  }

  const handleSearchChange = (key: string, value: string) => {
    setSearchQueries((prev: any) => ({ ...prev, [key]: value.toLowerCase() }))
  }

  const questionFilters = [
    {
      menuName: hiddenMenu.length > 0 ? `All` : 'All Questions',
      key: 'all',
      isLeaf: false,
      options: [],
      filterKey: 'all',
      filterOp: '',
    },
    {
      menuName: 'Subject',
      key: 'subject',
      isLeaf: true,
      options: allFilterValues?.subjects ?? [],
      filterKey: 'subject_id',
      filterOp: 'eq',
    },
    {
      menuName: 'Theme',
      key: 'theme',
      isLeaf: true,
      options: allFilterValues?.themes ?? [],
      filterKey: 'theme_id',
      filterOp: 'eq',
    },
    {
      menuName: 'Sub Theme',
      key: 'sub_theme',
      isLeaf: true,
      options: allFilterValues?.sub_themes ?? [],
      filterKey: 'sub_theme_id',
      filterOp: 'eq',
    },
    {
      menuName: 'Topic',
      key: 'topic',
      isLeaf: true,
      options: allFilterValues?.topics ?? [],
      filterKey: 'topic_id',
      filterOp: 'eq',
    },
    {
      menuName: 'Difficulty',
      key: 'difficulty_level',
      isLeaf: true,
      options: allFilterValues?.difficulty_levels ?? [],
      filterKey: 'difficulty_level_id',
      filterOp: 'eq',
    },
    {
      menuName: 'Blooms',
      key: 'blooms',
      isLeaf: true,
      options: allFilterValues?.blooms_levels ?? [],
      filterKey: 'bloom_level_id',
      filterOp: 'eq',
    },
    {
      menuName: 'Request Type',
      key: 'request type',
      isLeaf: true,
      options: [
        { id: 'Q', name: 'Created' },
        { id: 'Uploaded', name: 'Uploaded' },
      ],
      filterKey: 'request_type',
      filterOp: 'eq',
    },
    {
      menuName: 'Status',
      key: 'status',
      isLeaf: true,
      options: [
        { id: 'Open', name: 'Open' },
        { id: 'Open (Require Amending', name: 'Open (Require Amending)' },
        { id: 'Published', name: 'Published' },
        { id: 'Rejected at Approval', name: 'Rejected at Approval' },
        { id: 'Rejected at Review', name: 'Rejected at Review' },
        { id: 'Retired', name: 'Retired' },
        { id: 'Reviewed', name: 'Reviewed' },
      ],
      filterKey: 'status',
      filterOp: 'eq',
    },
  ]

  return (
    <>
      {/* Show selected filters */}
      {Object.keys(activeFilters).length > 0 && (
        <div style={{ marginBottom: '10px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {Object.entries(activeFilters).map(([filterKey, filterData]: any) => (
            <div
              key={filterKey}
              style={{
                backgroundColor: '#f0f0f0',
                borderRadius: '20px',
                padding: '5px 10px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span style={{ marginRight: '8px' }}>
                <strong>{filterData.key.replace(/_/g, ' ')}:</strong> {filterData.label}
              </span>
              <CloseCircleOutlined
                onClick={() => handleRemoveFilter(filterData?.key, filterKey, filterData.operator)}
                style={{ color: 'red', cursor: 'pointer' }}
              />
            </div>
          ))}
        </div>
      )}
      <Menu mode="horizontal" defaultSelectedKeys={['all']} style={{ background: '#ffffff' }}>
        {questionFilters.map((item, idx) =>
          !item.isLeaf ? (
            <Menu.Item
              key={`${item.key}${idx}`}
              icon={<Icons.AppstoreOutlined />}
              onClick={async () => {
                setActiveFilters({})
                setSelectedKeys(['all'])
                handleFilters({})
                if (setCurrentPageValue && fetchQuestions && limit) {
                  setCurrentPageValue(1)
                  await fetchQuestions(limit, 1, searchValue, userRole, userId, {})
                }
              }}
            >
              {item.menuName}
            </Menu.Item>
          ) : hiddenMenu?.includes(item.key) ? null : (
            <Menu.SubMenu
              key={`${item.key}${idx}`}
              title={item.menuName}
              icon={<FilterOutlined />}
              style={{
                color: selectedKeys.includes(item.key) ? 'blue' : '',
                backgroundColor: selectedKeys.includes(item.key) ? '#e6e6e6' : '',
                fontWeight: selectedKeys.includes(item.key) ? 'bold' : '',
                borderRadius: '50px',
              }}
            >
              <Menu.Item key={`search_${item.key}`} style={{ height: '100%', position: 'sticky', top: 0, zIndex: 1 }}>
                <div
                  style={{ padding: '2px', width: '90%', height: '100%', display: 'flex', alignItems: 'center' }}
                  onClick={e => e.stopPropagation()}
                >
                  <Input
                    placeholder={`Search ${item.menuName}`}
                    onChange={e => handleSearchChange(item.key, e.target.value)}
                    allowClear
                    style={{ width: '100%', paddingBottom: '10px' }}
                    prefix={<Icons.SearchOutlined />}
                  />
                </div>
              </Menu.Item>

              {item.options
                .filter((opt: any) => {
                  const searchVal = searchQueries[item.key] || ''
                  return opt.name.toLowerCase().includes(searchVal)
                })
                .map((opt: any, optIdx: number) => (
                  <Menu.Item
                    key={`${opt.name}${optIdx}`}
                    icon={<AppstoreOutlined />}
                    onClick={() => handleChange(item.filterKey, opt.id, item.filterOp, item.key, opt.name)}
                  >
                    {opt.name}
                  </Menu.Item>
                ))}

              {selectedKeys.includes(item.key) && (
                <Menu.Item
                  key={`remove_${item.key}`}
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleRemoveFilter(item.key, item.filterKey, item.filterOp)}
                  style={{ color: 'red' }}
                >
                  Remove {item.menuName}
                </Menu.Item>
              )}
            </Menu.SubMenu>
          )
        )}
      </Menu>
    </>
  )
}

export default React.memo(AllQuestionFilter)
